(() => {
  'use strict';

  const STORAGE_KEY = 'lieblingsplatz_click_collect_v1';
  const STATUS_LABELS = Object.freeze({
    available: ['Verfügbar', 'Dieser Artikel kann in der gewählten Filiale vorgemerkt werden.'],
    unavailable: ['Nicht verfügbar', 'Diese Variante ist derzeit in keiner Filiale verfügbar.'],
    unknown: ['Derzeit nicht prüfbar', 'Die Verfügbarkeit konnte nicht geprüft werden. Bitte versuche es später erneut.'],
  });

  function findVariant(variants, selectedOptions) {
    return variants.find((variant) => (
      variant.options.length === selectedOptions.length
      && variant.options.every((value, index) => value === selectedOptions[index])
    )) || null;
  }

  function addReservationItem(current, store, item) {
    const basket = current && current.version === 1
      ? structuredClone(current)
      : { version: 1, store: null, items: [] };

    if (basket.store && Number(basket.store.storeNo) !== Number(store.storeNo)) {
      return { basket, error: `Dieser Korb ist bereits der Filiale ${basket.store.storeLabel} zugeordnet.` };
    }

    basket.store = { storeNo: Number(store.storeNo), storeLabel: String(store.storeLabel) };
    const existing = basket.items.find((entry) => entry.variantId === item.variantId);
    if (existing) existing.quantity += 1;
    else basket.items.push({ ...item, quantity: 1 });
    return { basket, error: '' };
  }

  function emptyBasket() {
    return { version: 1, store: null, items: [] };
  }

  class ClickCollectProduct {
    constructor(root) {
      this.root = root;
      this.apiBase = root.dataset.apiBase.replace(/\/$/, '');
      this.product = JSON.parse(root.querySelector('[data-click-collect-product-json]').textContent);
      this.optionGroups = [...root.querySelectorAll('[data-product-option]')].map((group) => ({
        index: Number(group.dataset.optionIndex),
        buttons: [...group.querySelectorAll('[data-option-value]')],
      }));
      this.storeSelect = root.querySelector('[data-store-select]');
      this.reserveButton = root.querySelector('[data-reserve-button]');
      this.availabilityPanel = root.querySelector('[data-availability-panel]');
      this.availabilityTitle = root.querySelector('[data-availability-title]');
      this.availabilityMessage = root.querySelector('[data-availability-message]');
      this.basketItems = root.querySelector('[data-basket-items]');
      this.basketEmpty = root.querySelector('[data-basket-empty]');
      this.basketStore = root.querySelector('[data-basket-store]');
      this.basketTotal = root.querySelector('[data-basket-total]');
      this.basketError = root.querySelector('[data-basket-error]');
      this.basketClear = root.querySelector('[data-basket-clear]');
      this.reservationForm = root.querySelector('[data-reservation-form]');
      this.reservationSubmit = root.querySelector('[data-reservation-submit]');
      this.reservationStatus = root.querySelector('[data-reservation-status]');
      this.reservationFormTitle = root.querySelector('[data-reservation-form-title]');
      this.selectedOptions = this.product.options.map(() => '');
      this.selectedVariant = null;
      this.availabilityStatus = 'idle';
      this.requestSequence = 0;
      this.stores = new Map();
      this.basket = this.readBasket();
      this.idempotencyKey = '';
      this.submitted = false;
      this.bind();
      this.selectSingletonOptions();
      this.renderBasket();
      this.selectionChanged();
    }

    bind() {
      this.optionGroups.forEach((group) => group.buttons.forEach((button) => button.addEventListener('click', () => {
        if (button.disabled) return;
        this.selectedOptions[group.index] = button.dataset.optionValue;
        this.setPressed(group.buttons, button);
        this.updateOptionChoices();
        this.selectionChanged();
      })));
      this.storeSelect.addEventListener('change', () => this.storeSelectionChanged());
      this.reserveButton.addEventListener('click', () => this.addToBasket());
      this.basketClear.addEventListener('click', () => {
        this.basket = emptyBasket();
        this.writeBasket();
        this.renderBasket();
      });
      this.basketItems.addEventListener('click', (event) => {
        const button = event.target.closest('[data-remove-variant]');
        if (!button) return;
        this.basket.items = this.basket.items.filter((item) => item.variantId !== button.dataset.removeVariant);
        if (!this.basket.items.length) this.basket = emptyBasket();
        this.writeBasket();
        this.renderBasket();
      });
      this.reservationForm.addEventListener('submit', (event) => this.submitReservation(event));
    }

    selectSingletonOptions() {
      this.optionGroups.forEach((group) => {
        if (group.buttons.length !== 1) return;
        const button = group.buttons[0];
        this.selectedOptions[group.index] = button.dataset.optionValue;
        this.setPressed(group.buttons, button);
      });
      this.updateOptionChoices();
    }

    updateOptionChoices() {
      this.optionGroups.forEach((group) => group.buttons.forEach((button) => {
        const candidate = [...this.selectedOptions];
        candidate[group.index] = button.dataset.optionValue;
        button.disabled = !this.product.variants.some((variant) => variant.options.every(
          (value, index) => !candidate[index] || candidate[index] === value,
        ));
      }));
    }

    selectionChanged() {
      if (this.submitted) return;
      const previousStore = this.storeSelect.value;
      this.selectedVariant = findVariant(this.product.variants, this.selectedOptions);
      this.reserveButton.disabled = true;
      this.availabilityStatus = 'idle';
      this.reserveButton.textContent = 'Zur Reservierung hinzufügen';
      this.requestSequence += 1;
      this.stores.clear();
      this.storeSelect.disabled = true;
      if (this.selectedVariant) {
        this.root.querySelector('[data-product-price]').textContent = this.money(
          Number(this.selectedVariant.price), this.product.currency,
        );
      }
      if (!this.selectedVariant) {
        this.storeSelect.replaceChildren(new Option('Zuerst Farbe und Größe wählen', ''));
        this.setIdle();
        return;
      }
      this.storeSelect.replaceChildren(new Option('Verfügbare Filialen werden geladen …', ''));
      this.loadAvailableStores(this.selectedVariant, previousStore, this.requestSequence);
    }

    async loadAvailableStores(variant, previousStore, sequence) {
      this.availabilityPanel.dataset.status = 'loading';
      this.availabilityTitle.textContent = 'Verfügbarkeit wird geprüft …';
      this.availabilityMessage.textContent = 'Wir prüfen diese Variante in allen Filialen.';
      const adminVariantId = `gid://shopify/ProductVariant/${variant.id}`;
      const query = new URLSearchParams({ variant_id: adminVariantId });
      try {
        const response = await fetch(`${this.apiBase}/available-stores?${query}`, {
          credentials: 'omit',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('availability_unavailable');
        const result = await response.json();
        if (sequence !== this.requestSequence || result.variant_id !== adminVariantId) return;
        if (result.status === 'unknown' || !Array.isArray(result.stores)) {
          this.storeSelect.replaceChildren(new Option('Filialen derzeit nicht prüfbar', ''));
          this.setAvailability('unknown');
          return;
        }
        const stores = result.stores.filter((store) => (
          Number.isInteger(Number(store.store_no))
          && typeof store.store_name === 'string'
          && store.status === 'available'
        ));
        this.storeSelect.replaceChildren(new Option('Filiale auswählen', ''));
        stores.forEach((store) => {
          const storeNo = String(Number(store.store_no));
          const label = store.store_name;
          this.stores.set(storeNo, { storeNo: Number(store.store_no), storeLabel: label });
          this.storeSelect.add(new Option(label, storeNo));
        });
        if (!stores.length) {
          this.storeSelect.replaceChildren(new Option('Keine Filiale verfügbar', ''));
          this.setAvailability('unavailable');
          return;
        }
        this.storeSelect.disabled = false;
        if (stores.length === 1) this.storeSelect.value = String(Number(stores[0].store_no));
        else if (this.stores.has(previousStore)) this.storeSelect.value = previousStore;
        this.storeSelectionChanged();
      } catch (_error) {
        if (sequence !== this.requestSequence) return;
        this.storeSelect.replaceChildren(new Option('Filialen derzeit nicht prüfbar', ''));
        this.storeSelect.disabled = true;
        this.setAvailability('unknown');
      }
    }

    storeSelectionChanged() {
      if (!this.selectedVariant || !this.stores.has(this.storeSelect.value)) {
        this.availabilityStatus = 'idle';
        this.availabilityPanel.dataset.status = 'idle';
        this.availabilityTitle.textContent = 'Filiale auswählen';
        this.availabilityMessage.textContent = `Diese Variante ist in ${this.stores.size} Filialen verfügbar.`;
        this.reserveButton.disabled = true;
        return;
      }
      this.setAvailability('available');
    }

    setAvailability(status) {
      const copy = STATUS_LABELS[status] || STATUS_LABELS.unknown;
      this.availabilityStatus = status;
      this.availabilityPanel.dataset.status = status;
      this.availabilityTitle.textContent = copy[0];
      this.availabilityMessage.textContent = copy[1];
      this.reserveButton.disabled = status !== 'available';
    }

    setIdle() {
      this.availabilityPanel.dataset.status = 'idle';
      this.availabilityTitle.textContent = 'Auswahl vervollständigen';
      this.availabilityMessage.textContent = 'Wähle zuerst alle Variantenoptionen.';
    }

    addToBasket() {
      if (this.availabilityStatus !== 'available' || !this.selectedVariant) return;
      const store = this.stores.get(this.storeSelect.value);
      if (!store) return;
      const result = addReservationItem(this.basket, store, {
        productId: this.product.productId,
        variantId: `gid://shopify/ProductVariant/${this.selectedVariant.id}`,
        title: this.product.title,
        image: this.selectedVariant.image || this.product.image,
        selectedOptions: this.product.options.map((option, index) => ({
          name: option.name,
          value: this.selectedVariant.options[index],
        })),
        priceCents: Number(this.selectedVariant.price),
        currency: this.product.currency,
      });
      this.basketError.textContent = result.error;
      if (result.error) return;
      this.basket = result.basket;
      this.writeBasket();
      this.renderBasket();
      this.reserveButton.textContent = 'Im Reservierungskorb';
    }

    readBasket() {
      try {
        const value = JSON.parse(localStorage.getItem(STORAGE_KEY));
        return value && value.version === 1 && Array.isArray(value.items) ? value : emptyBasket();
      } catch (_error) {
        return emptyBasket();
      }
    }

    writeBasket() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.basket));
      } catch (_error) {
        this.basketError.textContent = 'Der lokale Reservierungskorb konnte nicht gespeichert werden.';
      }
    }

    renderBasket() {
      const hasItems = this.basket.items.length > 0;
      this.basketItems.replaceChildren();
      this.basketEmpty.hidden = hasItems;
      this.basketStore.hidden = !hasItems;
      this.basketTotal.hidden = !hasItems;
      this.basketClear.hidden = !hasItems;
      this.reservationForm.hidden = !hasItems;
      this.basketStore.textContent = hasItems ? `Filiale: ${this.basket.store.storeLabel}` : '';
      let total = 0;
      this.basket.items.forEach((item) => {
        total += item.priceCents * item.quantity;
        const row = document.createElement('li');
        row.className = 'cc-basket__item';
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = '';
        image.loading = 'lazy';
        const details = document.createElement('div');
        const title = document.createElement('strong');
        title.textContent = item.title;
        const choices = document.createElement('span');
        const selectedOptions = Array.isArray(item.selectedOptions)
          ? item.selectedOptions.map((option) => option.value)
          : [item.color, item.size].filter(Boolean);
        choices.textContent = `${selectedOptions.join(' · ')} · Menge ${item.quantity}`;
        const price = document.createElement('span');
        price.textContent = this.money(item.priceCents * item.quantity, item.currency);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.dataset.removeVariant = item.variantId;
        remove.textContent = 'Entfernen';
        details.append(title, choices, price, remove);
        row.append(image, details);
        this.basketItems.append(row);
      });
      if (hasItems) this.basketTotal.querySelector('strong').textContent = this.money(total, this.basket.items[0].currency);
    }

    async submitReservation(event) {
      event.preventDefault();
      if (!this.basket.items.length || !this.basket.store) return;
      this.reservationSubmit.disabled = true;
      this.reservationStatus.textContent = 'Anfrage wird sicher geprüft …';
      if (!this.idempotencyKey) this.idempotencyKey = crypto.randomUUID();
      const form = new FormData(this.reservationForm);
      const payload = {
        store_no: Number(this.basket.store.storeNo),
        customer: {
          name: String(form.get('name') || ''),
          email: String(form.get('email') || ''),
          phone: String(form.get('phone') || ''),
          note: String(form.get('note') || ''),
        },
        items: this.basket.items.map((item) => ({
          product_id: item.productId,
          variant_id: item.variantId,
          quantity: item.quantity,
        })),
      };
      try {
        const response = await fetch(`${this.apiBase}/reservations`, {
          method: 'POST',
          credentials: 'omit',
          headers: {
            'Content-Type': 'application/json',
            'Idempotency-Key': this.idempotencyKey,
            'X-Click-Collect-Request': '1',
          },
          body: JSON.stringify(payload),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.detail || 'reservation_failed');
        this.reservationStatus.textContent = `Anfrage ${result.reservation_no} ist eingegangen. Die Filiale prüft sie jetzt.`;
        this.basket = emptyBasket();
        this.writeBasket();
        this.renderBasket();
        this.submitted = true;
        this.basketEmpty.hidden = true;
        this.reserveButton.disabled = true;
        this.reserveButton.textContent = 'Reservierung angefragt';
        this.reservationForm.hidden = false;
        this.reservationForm.classList.add('is-complete');
        this.reservationFormTitle.textContent = 'Anfrage gesendet';
      } catch (_error) {
        this.reservationStatus.textContent = 'Die Anfrage konnte nicht gesendet werden. Bitte prüfe deine Angaben oder versuche es später erneut.';
        this.reservationSubmit.disabled = false;
      }
    }

    money(cents, currency) {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: currency || 'EUR' }).format(cents / 100);
    }

    setPressed(buttons, active) {
      buttons.forEach((button) => button.setAttribute('aria-pressed', String(button === active)));
    }
  }

  globalThis.ClickCollectProductPoc = Object.freeze({ findVariant, addReservationItem, emptyBasket, STATUS_LABELS });
  if (typeof document !== 'undefined') {
    document.querySelectorAll('[data-click-collect-product]').forEach((root) => new ClickCollectProduct(root));
  }
})();
